import { POPOVER_WRAPPER_PADDING } from '@ardoq/popovers';
import { Bounds } from '@ardoq/graph';
import { BlocksViewGroup, LinksAggregation } from 'tabview/blocks/types';
import { applyLayout } from 'tabview/blocks/layout/layout';
import { renderGraph } from 'tabview/blocks/visual/visual';
import { createProjection } from 'tabview/blocks/misc/projection';
import { cloneSubGraph } from './cloneSubGraph';
import { rectBloat, rectHeight, rectWidth } from 'tabview/blocks/misc/geometry';
import { BLOCKS_VIEW_ROUTINGCHANNEL_MINIMUM_SIZE as ROUTINGCHANNEL_MINIMUM_SIZE } from 'tabview/blocks/consts';

type Size = [width: number, height: number];

const limitViewport = (
  worldWidth: number,
  worldHeight: number,
  isColumnarLayout?: boolean
): Size => {
  const aspect = worldWidth / worldHeight;
  // take 448px width if the graph is wider. otherwise 208px width.
  const initialWidth =
    worldWidth > worldHeight && !isColumnarLayout ? 448 : 208;
  // calculate height based on width, respecting aspect ratio.
  const initialHeight = initialWidth / aspect;
  // reduce the height if it was too tall.
  const height = Math.min(initialHeight, 448 - 2 * POPOVER_WRAPPER_PADDING);
  // calculate the final width via aspect ratio from the final and limited height.
  const width = height * aspect;
  return [width, height];
};

/**
 * The renderer used by the group tooltip to display the group contents of open or closed groups.
 *
 * @returns a function that takes a {@link HTMLCanvasElement} and draws the contents of {@link node}, including children and their links, and resizes the canvas.
 */
const groupContentRenderer =
  (
    node: BlocksViewGroup,
    linksAggregation: LinksAggregation,
    isColumnarLayout: boolean
  ) =>
  (canvas: HTMLCanvasElement) => {
    const graph = cloneSubGraph({ ...node, open: true, parent: null });
    applyLayout(graph, linksAggregation);
    const {
      root: { content: rootContent },
    } = graph;
    if (!rootContent) {
      return;
    }
    /**
     * the world rect, which all graph coordinates exist in.
     *
     * this is based on the root content rect, but an outer margin is removed. that margin was routing channels which we don't need. any references exiting the subgraph were removed.
     */
    const world = rectBloat(rootContent, -ROUTINGCHANNEL_MINIMUM_SIZE);
    const worldWidth = rectWidth(world);
    const worldHeight = rectHeight(world);
    const [viewportWidth, viewportHeight] = limitViewport(
      worldWidth,
      worldHeight,
      isColumnarLayout
    );
    canvas.width = viewportWidth;
    canvas.height = viewportHeight;
    canvas.style.width = `${canvas.width}px`;
    canvas.style.height = `${canvas.height}px`;
    const projection = createProjection();
    const viewport: Bounds = [0, 0, viewportWidth, viewportHeight];
    projection.adjustToViewport(viewport, 1);
    projection.adjustToRect(world, 0, false);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }
    ctx.setTransform(projection.matrix());
    renderGraph(graph, projection.window(false), 'normal', ctx);
  };

export { groupContentRenderer };
